import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Stack,
  Alert,
  LinearProgress,
  Badge,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  ShoppingCart,
  TrendingUp,
  Warning,
  CheckCircle,
  Close,
  Refresh,
  Settings,
  TrendingDown,
  Info,
  ShowChart,
  Close as CloseIcon,
  History,
} from '@mui/icons-material';
import { Recommendation, Portfolio, Position } from '../types';
import { RecommendationService } from '../services/recommendationService';
import { PortfolioService } from '../services/portfolioService';
import { PositionService } from '../services/positionService';
import { format } from 'date-fns';
import { StockChart } from '../components/StockChart';
import { useStockHistory } from '../hooks/useStockHistory';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import RecommendationExecutionDialog from '../components/RecommendationExecutionDialog';
import RecommendationExitDialog from '../components/RecommendationExitDialog';
import ExecutedRecommendationList from '../components/ExecutedRecommendationList';
import StockInfoDialog from '../components/StockInfoDialog';
import { useMobileDetect } from '../hooks/useMobileDetect';
import { MobileRecommendationCard } from '../components/mobile';
import { StockService } from '../services/stockService';
import { WatchlistEntry } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`recommendation-tabpanel-${index}`}
      aria-labelledby={`recommendation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Recommendations: React.FC = () => {
  const { isMobile } = useMobileDetect();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [executedRecommendations, setExecutedRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [executedLoading, setExecutedLoading] = useState(false);
  const [executedLoaded, setExecutedLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [mobileTab, setMobileTab] = useState<'active' | 'executed'>('active');
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);
  const [positions, setPositions] = useState<Record<string, Position[]>>({});
  const [pendingPositions, setPendingPositions] = useState<Record<string, Position[]>>({});
  const [watchlistData, setWatchlistData] = useState<Record<string, WatchlistEntry>>({});
  const [chartDialog, setChartDialog] = useState<{
    open: boolean;
    symbol: string | null;
    pivotPrice?: number;
    currentPrice: number;
    priceChangePercent: number;
  }>({ open: false, symbol: null, pivotPrice: undefined, currentPrice: 0, priceChangePercent: 0 });
  const [executionDialog, setExecutionDialog] = useState<{
    open: boolean;
    recommendation: Recommendation | null;
    portfolio: Portfolio | null;
    availableFunds: number;
  }>({ open: false, recommendation: null, portfolio: null, availableFunds: 0 });
  const [exitDialog, setExitDialog] = useState<{
    open: boolean;
    recommendation: Recommendation | null;
    position: Position | null;
    portfolio: Portfolio | null;
  }>({ open: false, recommendation: null, position: null, portfolio: null });
  const [infoDialog, setInfoDialog] = useState<{
    open: boolean;
    symbol: string | null;
  }>({ open: false, symbol: null });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedPortfolioId) {
      loadRecommendations();
      // Reset executed loaded flag when portfolio changes
      setExecutedLoaded(false);
    }
  }, [selectedPortfolioId]);

  // Lazy load executed recommendations when Executed tab (index 3) is selected, or mobile executed tab
  useEffect(() => {
    const shouldLoadExecuted = (tabValue === 3 || mobileTab === 'executed') && !executedLoaded && !executedLoading;
    if (shouldLoadExecuted) {
      loadExecutedRecommendations();
    }
  }, [tabValue, mobileTab, executedLoaded, executedLoading]);

  const loadData = async () => {
    try {
      // Load portfolios
      const allPortfolios = await PortfolioService.getActivePortfolios();
      
      // Sort portfolios: Real first, then Virtual, both by creation date ascending
      const sortedPortfolios = allPortfolios.sort((a, b) => {
        // First sort by type (real before virtual)
        if (a.type !== b.type) {
          return a.type === 'real' ? -1 : 1;
        }
        // Then sort by creation date (ascending - oldest first)
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateA.getTime() - dateB.getTime();
      });
      
      setPortfolios(sortedPortfolios);
      
      // Load positions for each portfolio
      const positionsMap: Record<string, Position[]> = {};
      const pendingMap: Record<string, Position[]> = {};
      
      for (const portfolio of allPortfolios) {
        const [openPositions, pending] = await Promise.all([
          PositionService.getOpenPositions(portfolio.id),
          PositionService.getPendingPositions(portfolio.id)
        ]);
        positionsMap[portfolio.id] = openPositions;
        pendingMap[portfolio.id] = pending;
      }
      
      setPositions(positionsMap);
      setPendingPositions(pendingMap);

      // Load watchlist data for real-time price/volume display
      try {
        const watchlist = await StockService.getWatchlist(false);
        const watchlistMap: Record<string, WatchlistEntry> = {};
        watchlist.forEach(entry => {
          watchlistMap[entry.symbol] = entry;
        });
        setWatchlistData(watchlistMap);
      } catch (watchlistError) {
        console.error('Error loading watchlist data:', watchlistError);
      }

      // Only select first portfolio by default if none is currently selected
      // This preserves the user's selection when reloading after trade execution
      if (allPortfolios.length > 0 && !selectedPortfolioId) {
        setSelectedPortfolioId(allPortfolios[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadRecommendations = async () => {
    try {
      setLoading(true);

      // Load recommendations for selected portfolio or all
      const recs = selectedPortfolioId === 'all'
        ? await RecommendationService.getAllActiveRecommendations()
        : await RecommendationService.getPortfolioRecommendations(selectedPortfolioId!);

      setRecommendations(recs);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExecutedRecommendations = async () => {
    try {
      setExecutedLoading(true);

      const executed = await RecommendationService.getExecutedRecommendations(
        selectedPortfolioId === 'all' ? undefined : selectedPortfolioId || undefined,
        30
      );

      setExecutedRecommendations(executed);
      setExecutedLoaded(true);
    } catch (error) {
      console.error('Error loading executed recommendations:', error);
    } finally {
      setExecutedLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Trigger new recommendation generation
      await RecommendationService.generateAndStoreRecommendations('manual');
      // Reload the list
      await loadRecommendations();
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExecute = async (recommendation: Recommendation) => {
    const portfolio = portfolios.find(p => p.id === recommendation.portfolioId);
    if (!portfolio) {
      console.error('Portfolio not found for recommendation');
      return;
    }

    // Handle exit recommendations differently
    if (recommendation.type === 'exit' && recommendation.positionId) {
      // Find the position
      const portfolioPositions = positions[portfolio.id] || [];
      const position = portfolioPositions.find(p => p.id === recommendation.positionId);
      
      if (position) {
        setExitDialog({
          open: true,
          recommendation,
          position,
          portfolio,
        });
        return;
      }
    }

    // Handle entry recommendations
    const metrics = calculatePortfolioMetrics(portfolio);
    
    setExecutionDialog({
      open: true,
      recommendation,
      portfolio,
      availableFunds: metrics.availableFunds,
    });
  };

  const handleTradeExecuted = async () => {
    // Reload data to update portfolio stats
    await loadData();
    // Reload recommendations
    await loadRecommendations();
  };

  const handleDismiss = async (recommendationId: string) => {
    try {
      await RecommendationService.dismissRecommendation(recommendationId);
      await loadRecommendations();
    } catch (error) {
      console.error('Error dismissing recommendation:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  const calculatePortfolioMetrics = (portfolio: Portfolio) => {
    const portfolioPositions = positions[portfolio.id] || [];
    const portfolioPendingPositions = pendingPositions[portfolio.id] || [];
    
    // Calculate costs
    const positionsCost = portfolioPositions.reduce(
      (sum, p) => sum + p.entryPrice * p.quantity,
      0
    );
    
    const pendingCost = portfolioPendingPositions.reduce(
      (sum, p) => sum + (p.limitPrice || p.entryPrice) * p.quantity,
      0
    );
    
    // Calculate available funds
    const realizedLosses = Math.min(0, portfolio.totalEarnings || 0);
    const availableFunds = portfolio.initialFundValue - positionsCost + realizedLosses;
    const availableFundsPending = availableFunds - pendingCost;
    
    return {
      availableFunds,
      availableFundsPending,
      positionCount: portfolioPositions.length,
      maxPositions: portfolio.maxPositions,
      totalEarnings: portfolio.totalEarnings || 0
    };
  };

  const getActionIcon = (recommendation: Recommendation) => {
    if (recommendation.type === 'entry') return <ShoppingCart />;
    if (recommendation.type === 'exit') return <TrendingUp />;
    if (recommendation.type === 'adjustment') return <Settings />;
    if (recommendation.type === 'alert') return <Warning />;
    return <Info />;
  };

  const getRecommendationsByType = (type: string) => {
    return recommendations.filter(r => r.type === type);
  };

  const entryRecs = getRecommendationsByType('entry');
  const exitRecs = getRecommendationsByType('exit');
  const adjustmentRecs = getRecommendationsByType('adjustment');
  const alertRecs = getRecommendationsByType('alert');

  // Combine all active recommendations for mobile view (single scrollable list)
  const allActiveRecs = [...entryRecs, ...exitRecs, ...adjustmentRecs, ...alertRecs].sort(
    (a, b) => b.score - a.score
  );

  // Mobile view rendering function
  const renderMobileView = () => {
    const currentPortfolio = portfolios.find(p => p.id === selectedPortfolioId);
    const metrics = currentPortfolio ? calculatePortfolioMetrics(currentPortfolio) : null;

    return (
      <Box sx={{ p: 1.5, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h5">Recommendations</Typography>
          <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
            <Refresh />
          </IconButton>
        </Box>

        {refreshing && <LinearProgress sx={{ mb: 1 }} />}

        {/* Portfolio Selector (scrollable chips) */}
        <Box sx={{ mb: 1.5, overflowX: 'auto', whiteSpace: 'nowrap', pb: 1 }}>
          <Stack direction="row" spacing={0.5}>
            {portfolios.map(p => (
              <Chip
                key={p.id}
                label={p.name}
                size="small"
                onClick={() => setSelectedPortfolioId(p.id)}
                color={selectedPortfolioId === p.id ? 'primary' : 'default'}
                variant={selectedPortfolioId === p.id ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>

        {/* Compact Portfolio Stats */}
        {metrics && (
          <Paper sx={{ p: 1, mb: 1.5, bgcolor: 'primary.50', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-around" alignItems="center">
              <Box sx={{ textAlign: 'center', minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Available</Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  fontSize="0.8rem"
                  noWrap
                  color={metrics.availableFundsPending < 0 ? 'error' : 'textPrimary'}
                >
                  {formatCurrency(metrics.availableFundsPending)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center', minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Positions</Typography>
                <Typography variant="body2" fontWeight="medium" fontSize="0.8rem">
                  {metrics.positionCount}/{metrics.maxPositions}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center', minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="textSecondary" fontSize="0.65rem">Earnings</Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  fontSize="0.8rem"
                  noWrap
                  color={metrics.totalEarnings >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(metrics.totalEarnings)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {/* Active/Executed Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
          <Tabs
            value={mobileTab}
            onChange={(_, newValue) => setMobileTab(newValue)}
            variant="fullWidth"
          >
            <Tab
              label={`Active (${allActiveRecs.length})`}
              value="active"
            />
            <Tab
              label={`Executed (${executedRecommendations.length})`}
              value="executed"
            />
          </Tabs>
        </Box>

        {/* Recommendations List */}
        <Box sx={{ maxHeight: 'calc(100vh - 330px)', overflowY: 'auto' }}>
          {mobileTab === 'active' ? (
            allActiveRecs.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  No active recommendations
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Recommendations appear when stocks meet entry/exit criteria
                </Typography>
              </Paper>
            ) : (
              allActiveRecs.map(rec => {
                // Find matching position for exit recommendations
                const currentPositions = selectedPortfolioId ? positions[selectedPortfolioId] : [];
                const matchingPosition = rec.type === 'exit' && rec.positionId
                  ? currentPositions?.find(p => p.id === rec.positionId)
                  : undefined;

                return (
                  <MobileRecommendationCard
                    key={rec.id}
                    recommendation={rec}
                    currentWatchlistData={watchlistData[rec.symbol]}
                    position={matchingPosition}
                    onExecute={handleExecute}
                    onDismiss={handleDismiss}
                    onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
                      setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
                    }
                    onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
                  />
                );
              })
            )
          ) : (
            executedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : executedRecommendations.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  No executed recommendations
                </Typography>
              </Paper>
            ) : (
              <ExecutedRecommendationList
                recommendations={executedRecommendations}
                formatCurrency={formatCurrency}
                onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
                  setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
                }
                onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
              />
            )
          )}
        </Box>
      </Box>
    );
  };

  if (loading && !recommendations.length) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading recommendations...</Typography>
      </Box>
    );
  }

  // Render mobile view for small screens
  if (isMobile) {
    return (
      <>
        {renderMobileView()}

        {/* Dialogs - shared between mobile and desktop */}
        <StockChartDialog
          chartDialog={chartDialog}
          onClose={() => setChartDialog({ ...chartDialog, open: false })}
        />
        <RecommendationExecutionDialog
          open={executionDialog.open}
          onClose={() => setExecutionDialog({ ...executionDialog, open: false })}
          recommendation={executionDialog.recommendation}
          portfolio={executionDialog.portfolio}
          availableFunds={executionDialog.availableFunds}
          onTradeExecuted={handleTradeExecuted}
        />
        <RecommendationExitDialog
          open={exitDialog.open}
          onClose={() => setExitDialog({ ...exitDialog, open: false })}
          recommendation={exitDialog.recommendation}
          position={exitDialog.position}
          portfolio={exitDialog.portfolio}
          onPositionClosed={handleTradeExecuted}
        />
        <StockInfoDialog
          open={infoDialog.open}
          onClose={() => setInfoDialog({ open: false, symbol: null })}
          symbol={infoDialog.symbol}
        />
      </>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Trade Recommendations
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing} 
            variant="outlined"
            startIcon={<Refresh />}
          >
            Check Recommendations
          </Button>
        </Box>
      </Box>

      {refreshing && <LinearProgress sx={{ mb: 2 }} />}

      {/* Portfolio Selector */}
      {portfolios.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={selectedPortfolioId || portfolios[0]?.id}
            onChange={(_, value) => setSelectedPortfolioId(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {portfolios.map(p => (
              <Tab key={p.id} label={p.name} value={p.id} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Portfolio Stats Summary */}
      {selectedPortfolioId && selectedPortfolioId !== 'all' && (() => {
        const portfolio = portfolios.find(p => p.id === selectedPortfolioId);
        if (!portfolio) return null;
        
        const metrics = calculatePortfolioMetrics(portfolio);
        
        return (
          <Paper sx={{ p: 2.5, mb: 3, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
            <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-evenly">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Available Funds (w/Pending)
                </Typography>
                <Typography 
                  variant="h5" 
                  fontWeight="medium"
                  color={metrics.availableFundsPending < 0 ? 'error' : 'textPrimary'}
                >
                  {formatCurrency(metrics.availableFundsPending)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Positions
                </Typography>
                <Typography variant="h5" fontWeight="medium">
                  {metrics.positionCount} / {metrics.maxPositions}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Total Earnings
                </Typography>
                <Typography 
                  variant="h5" 
                  fontWeight="medium"
                  color={metrics.totalEarnings >= 0 ? 'success.main' : 'error.main'}
                >
                  {metrics.totalEarnings >= 0 ? '+' : ''}{formatCurrency(metrics.totalEarnings)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        );
      })()}

      {/* Always show tabs so users can navigate to Executed history even when no active recommendations */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab
            label={
              <Badge badgeContent={entryRecs.length} color="success">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart />
                  Entry
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={exitRecs.length} color="error">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDown />
                  Exit
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={adjustmentRecs.length} color="info">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Settings />
                  Adjustments
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={executedRecommendations.length} color="default">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History />
                  Executed
                </Box>
              </Badge>
            }
          />
          {alertRecs.length > 0 && (
            <Tab
              label={
                <Badge badgeContent={alertRecs.length} color="warning">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning />
                    Alerts
                  </Box>
                </Badge>
              }
            />
          )}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <RecommendationList
          recommendations={entryRecs}
          watchlistData={watchlistData}
          onExecute={handleExecute}
          onDismiss={handleDismiss}
          formatCurrency={formatCurrency}
          getUrgencyColor={getUrgencyColor}
          type="entry"
          onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
            setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
          }
          onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <RecommendationList
          recommendations={exitRecs}
          watchlistData={watchlistData}
          positions={selectedPortfolioId ? positions[selectedPortfolioId] : []}
          onExecute={handleExecute}
          onDismiss={handleDismiss}
          formatCurrency={formatCurrency}
          getUrgencyColor={getUrgencyColor}
          type="exit"
          onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
            setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
          }
          onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <RecommendationList
          recommendations={adjustmentRecs}
          watchlistData={watchlistData}
          onExecute={handleExecute}
          onDismiss={handleDismiss}
          formatCurrency={formatCurrency}
          getUrgencyColor={getUrgencyColor}
          type="adjustment"
          onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
            setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
          }
          onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {executedLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ExecutedRecommendationList
            recommendations={executedRecommendations}
            formatCurrency={formatCurrency}
            onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
              setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
            }
            onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
          />
        )}
      </TabPanel>

      {alertRecs.length > 0 && (
        <TabPanel value={tabValue} index={4}>
          <RecommendationList
            recommendations={alertRecs}
            watchlistData={watchlistData}
            onExecute={handleExecute}
            onDismiss={handleDismiss}
            formatCurrency={formatCurrency}
            getUrgencyColor={getUrgencyColor}
            type="alert"
            onChartOpen={(symbol, pivotPrice, currentPrice, priceChangePercent) =>
              setChartDialog({ open: true, symbol, pivotPrice, currentPrice, priceChangePercent })
            }
            onInfoOpen={(symbol) => setInfoDialog({ open: true, symbol })}
          />
        </TabPanel>
      )}

      {/* Stock Chart Dialog */}
      <StockChartDialog
        chartDialog={chartDialog}
        onClose={() => setChartDialog({ ...chartDialog, open: false })}
      />

      {/* Recommendation Execution Dialog */}
      <RecommendationExecutionDialog
        open={executionDialog.open}
        onClose={() => setExecutionDialog({ ...executionDialog, open: false })}
        recommendation={executionDialog.recommendation}
        portfolio={executionDialog.portfolio}
        availableFunds={executionDialog.availableFunds}
        onTradeExecuted={handleTradeExecuted}
      />

      {/* Recommendation Exit Dialog */}
      <RecommendationExitDialog
        open={exitDialog.open}
        onClose={() => setExitDialog({ ...exitDialog, open: false })}
        recommendation={exitDialog.recommendation}
        position={exitDialog.position}
        portfolio={exitDialog.portfolio}
        onPositionClosed={handleTradeExecuted}
      />

      {/* Stock Info Dialog */}
      <StockInfoDialog
        open={infoDialog.open}
        onClose={() => setInfoDialog({ open: false, symbol: null })}
        symbol={infoDialog.symbol}
      />
    </Box>
  );
};

// Separate component for recommendation list
interface RecommendationListProps {
  recommendations: Recommendation[];
  watchlistData?: Record<string, WatchlistEntry>;  // Current real-time data from watchlist
  positions?: Position[];  // Open positions for P/L calculation on exit recs
  onExecute: (rec: Recommendation) => void;
  onDismiss: (id: string) => void;
  formatCurrency: (value: number) => string;
  getUrgencyColor: (urgency: string) => 'error' | 'warning' | 'info' | 'default';
  type: string;
  onChartOpen: (symbol: string, pivotPrice: number | undefined, currentPrice: number, priceChangePercent: number) => void;
  onInfoOpen: (symbol: string) => void;
}

const RecommendationList: React.FC<RecommendationListProps> = ({
  recommendations,
  watchlistData = {},
  positions = [],
  onExecute,
  onDismiss,
  formatCurrency,
  getUrgencyColor,
  type,
  onChartOpen,
  onInfoOpen,
}) => {
  // Helper to find position for exit recommendations
  const getPositionForRec = (rec: Recommendation): Position | undefined => {
    if (rec.type !== 'exit' || !rec.positionId) return undefined;
    return positions.find(p => p.id === rec.positionId);
  };

  // Calculate days held from entry date
  const calculateDaysHeld = (entryDate: Date): number => {
    const now = new Date();
    const entry = new Date(entryDate);
    const diffTime = Math.abs(now.getTime() - entry.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };
  if (recommendations.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No {type} recommendations
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {type === 'entry' && 'Stocks will appear here when they approach pivot points'}
          {type === 'exit' && 'Positions will appear here when exit conditions are met'}
          {type === 'adjustment' && 'Position adjustments will appear here when needed'}
          {type === 'alert' && 'Market alerts will appear here'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {recommendations.map((rec) => {
        // Get current real-time data from watchlist
        const currentData = watchlistData[rec.symbol];
        const currentPrice = currentData?.currentPrice ?? rec.currentPrice;
        const priceChgPercent = currentData?.priceChgPercent;
        const volumeChgVs50Day = currentData?.volumeChgVs50Day;

        return (
        <Card key={rec.id} elevation={rec.urgency === 'immediate' ? 8 : 2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">{rec.symbol}</Typography>
                  {/* Current Price & Change from Watchlist */}
                  <Chip
                    label={formatCurrency(currentPrice)}
                    size="small"
                    variant="filled"
                    color="primary"
                  />
                  {priceChgPercent !== undefined && (
                    <Chip
                      label={`${priceChgPercent >= 0 ? '+' : ''}${priceChgPercent.toFixed(2)}%`}
                      size="small"
                      color={priceChgPercent >= 0 ? 'success' : 'error'}
                      icon={priceChgPercent >= 0 ? <TrendingUp /> : <TrendingDown />}
                    />
                  )}
                  {volumeChgVs50Day !== undefined && (
                    <Chip
                      label={`Vol: ${volumeChgVs50Day > 0 ? '+' : ''}${volumeChgVs50Day.toFixed(0)}%`}
                      size="small"
                      color={volumeChgVs50Day > 50 ? 'success' : volumeChgVs50Day > 0 ? 'default' : 'error'}
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={rec.urgency}
                    color={getUrgencyColor(rec.urgency)}
                    size="small"
                  />
                  {rec.urgency === 'immediate' && <Warning color="error" />}
                  <Chip
                    label={`Score: ${rec.score}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {/* Entry Recommendation Details */}
                {rec.type === 'entry' && (
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      Buy {rec.recommendedQuantity} shares
                    </Typography>
                    <Stack spacing={0.5} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Entry: {formatCurrency(rec.recommendedEntryPrice || rec.currentPrice)} 
                        {rec.orderType && ` (${rec.orderType} order)`}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Stop Loss: {formatCurrency(rec.recommendedStopLoss || 0)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Target: {formatCurrency(rec.recommendedTarget || 0)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Cost: {formatCurrency((rec.recommendedQuantity || 0) * (rec.recommendedEntryPrice || rec.currentPrice))}
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {/* Exit Recommendation Details */}
                {rec.type === 'exit' && (() => {
                  const position = getPositionForRec(rec);
                  const entryPrice = position?.entryPrice;
                  const quantity = position?.quantity || rec.recommendedQuantity;
                  const daysHeld = position?.entryDate ? calculateDaysHeld(position.entryDate) : position?.holdingDays;
                  const profitLoss = entryPrice && quantity
                    ? (currentPrice - entryPrice) * quantity
                    : undefined;
                  const profitLossPercent = entryPrice
                    ? ((currentPrice - entryPrice) / entryPrice) * 100
                    : undefined;

                  return (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        {rec.exitReason === 'stop_hit' && 'Stop Loss Hit - Exit Immediately'}
                        {rec.exitReason === 'target_hit' && 'Target Reached - Take Profits'}
                        {rec.exitReason === 'time_limit' && 'Holding Time Exceeded'}
                        {rec.exitReason === 'stagnant' && 'Position Stagnant - Free Up Capital'}
                      </Typography>
                      <Stack spacing={0.5} sx={{ mb: 1 }}>
                        {/* P/L and Days Held - prominent display */}
                        {(profitLoss !== undefined || daysHeld !== undefined) && (
                          <Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
                            {profitLoss !== undefined && (
                              <Chip
                                label={`${profitLoss >= 0 ? '+' : ''}${formatCurrency(profitLoss)} (${profitLossPercent! >= 0 ? '+' : ''}${profitLossPercent!.toFixed(1)}%)`}
                                color={profitLoss >= 0 ? 'success' : 'error'}
                                size="small"
                                icon={profitLoss >= 0 ? <TrendingUp /> : <TrendingDown />}
                              />
                            )}
                            {daysHeld !== undefined && (
                              <Chip
                                label={`${daysHeld} day${daysHeld !== 1 ? 's' : ''} held`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        )}
                        {entryPrice && (
                          <Typography variant="body2" color="textSecondary">
                            Entry: {formatCurrency(entryPrice)} → Current: {formatCurrency(currentPrice)}
                          </Typography>
                        )}
                        {quantity && (
                          <Typography variant="body2" color="textSecondary">
                            Position Size: {quantity} shares
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })()}

                {/* Adjustment Recommendation Details */}
                {rec.type === 'adjustment' && (
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      {rec.adjustmentType === 'raise_stop' && `Raise stop loss to ${formatCurrency(rec.adjustmentValue || 0)}`}
                      {rec.adjustmentType === 'lower_target' && `Lower target to ${formatCurrency(rec.adjustmentValue || 0)}`}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Current Price: {formatCurrency(rec.currentPrice)}
                    </Typography>
                  </Box>
                )}

                {/* Reasons */}
                <Box sx={{ mt: 2 }}>
                  {rec.reasons.map((reason, i) => (
                    <Typography key={i} variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      • {reason}
                    </Typography>
                  ))}
                </Box>

                {/* Technical Factors */}
                {rec.technicalFactors && Object.keys(rec.technicalFactors).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                      Technical Factors: {Object.entries(rec.technicalFactors)
                        .map(([key, value]) => `${key}: ${typeof value === 'number' ? value.toFixed(2) : value}`)
                        .join(' | ')}
                    </Typography>
                  </Box>
                )}

                {/* Timestamp */}
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Generated: {format(rec.generatedAt, 'MMM d, h:mm a')}
                  {rec.expiresAt && ` • Expires: ${format(rec.expiresAt, 'MMM d, h:mm a')}`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => onChartOpen(rec.symbol, undefined, rec.currentPrice, 0)}
                    title="View chart"
                  >
                    <ShowChart />
                  </IconButton>
                  <Tooltip title="View on Finviz">
                    <IconButton
                      size="small"
                      component="a"
                      href={`https://finviz.com/quote.ashx?t=${rec.symbol}&p=d`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ p: 0.5 }}
                    >
                      <img
                        src="https://finviz.com/favicon.ico"
                        alt="Finviz"
                        style={{ width: 18, height: 18 }}
                      />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    color="default"
                    onClick={() => onInfoOpen(rec.symbol)}
                    title="View stock info"
                  >
                    <Info />
                  </IconButton>
                </Box>
                <Button 
                  variant="contained" 
                  color={rec.type === 'exit' ? 'error' : 'success'}
                  onClick={() => onExecute(rec)}
                  startIcon={<CheckCircle />}
                >
                  {rec.type === 'entry' && 'Execute Buy'}
                  {rec.type === 'exit' && 'Close Position'}
                  {rec.type === 'adjustment' && 'Apply'}
                  {rec.type === 'alert' && 'Acknowledge'}
                </Button>
                <Tooltip title="Dismiss recommendation">
                  <IconButton onClick={() => onDismiss(rec.id)} color="default">
                    <Close />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
        );
      })}
    </Stack>
  );
};

interface StockChartDialogProps {
  chartDialog: {
    open: boolean;
    symbol: string | null;
    pivotPrice?: number;
    currentPrice: number;
    priceChangePercent: number;
  };
  onClose: () => void;
}

function StockChartDialog({ chartDialog, onClose }: StockChartDialogProps) {
  const {
    data,
    loading,
    error,
    pivotPrice,
    currentPrice: hookCurrentPrice,
    priceChgPercent: hookPriceChgPercent,
    lastUpdated,
    dataDelay,
    currentVolume,
    volumeChgVs50Day
  } = useStockHistory(chartDialog.symbol || '', 30);

  // Use hook values as source of truth, fall back to props if not available
  const displayCurrentPrice = hookCurrentPrice ?? chartDialog.currentPrice;
  const displayPriceChgPercent = hookPriceChgPercent ?? chartDialog.priceChangePercent;

  return (
    <Dialog
      open={chartDialog.open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Historical Price Chart</Typography>
          <IconButton
            onClick={onClose}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {!loading && !error && chartDialog.symbol && (
          <StockChart
            symbol={chartDialog.symbol}
            data={data}
            pivotPrice={pivotPrice}
            currentPrice={displayCurrentPrice}
            priceChangePercent={displayPriceChgPercent}
            lastUpdated={lastUpdated}
            dataDelay={dataDelay}
            currentVolume={currentVolume}
            volumeChgVs50Day={volumeChgVs50Day}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default Recommendations;